import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Tag, Plus, X, Loader2, ChevronDown } from 'lucide-react';
import api from '../../../../api/client';
import ShowDescriptionDialog from '../../component/ShowDescriptionDialog';
import { convertMarkdownToHtml } from '../../../../utils/markdownToHTML';
import parse from 'html-react-parser';

export default function CandidateJDTagsSection({
    candidateData,
    setCandidateData,
    candidateId,
    jobDescriptions,
    setSnackbar,
}) {
    const { t } = useTranslation();
    const [adding, setAdding] = useState(false);
    const [removingId, setRemovingId] = useState(null);
    const [addSelectValue, setAddSelectValue] = useState('');
    const [jdDescriptionModal, setJdDescriptionModal] = useState({ open: false, title: '', content: null });
    const [jdDescriptionLoading, setJdDescriptionLoading] = useState(false);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [jdSearch, setJdSearch] = useState('');
    const buttonRef = useRef(null);
    const dropdownListRef = useRef(null);

    // Close on outside click
    useEffect(() => {
        if (!isDropdownOpen) return;
        const handleClose = (event) => {
            if (
                buttonRef.current && !buttonRef.current.contains(event.target) &&
                dropdownListRef.current && !dropdownListRef.current.contains(event.target)
            ) {
                setIsDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClose);
        return () => {
            document.removeEventListener('mousedown', handleClose);
        };
    }, [isDropdownOpen]);

    const jdTags = candidateData?.jd_tags || [];
    const attachedJdIds = new Set((jdTags || []).map((tag) => tag.job_description_id));
    const availableJDs = (jobDescriptions || []).filter((jd) => !attachedJdIds.has(jd.id));

    const handleAdd = async () => {
        const jdId = addSelectValue ? parseInt(addSelectValue, 10) : null;
        if (!jdId || !candidateId) return;
        setAdding(true);
        try {
            const res = await api.post(`admin/candidate/${candidateId}/jd-tags/`, {
                job_description_id: jdId,
            }, {
                headers: { Authorization: `Token ${localStorage.getItem('adminToken')}` },
            });
            const newTag = res.data;
            setCandidateData((prev) => ({
                ...prev,
                jd_tags: [...(prev?.jd_tags || []), newTag],
            }));
            setAddSelectValue('');
            setJdSearch('');
            setSnackbar({
                open: true,
                message: t('candidateDetails.jdTagAdded'),
                severity: 'success',
            });
        } catch (err) {
            setSnackbar({
                open: true,
                message: err.response?.data?.error || t('candidateDetails.jdTagAddFailed'),
                severity: 'error',
            });
        } finally {
            setAdding(false);
        }
    };

    const handleRemove = async (jobDescriptionId) => {
        if (!candidateId) return;
        setRemovingId(jobDescriptionId);
        try {
            await api.delete(`admin/candidate/${candidateId}/jd-tags/${jobDescriptionId}/`, {
                headers: { Authorization: `Token ${localStorage.getItem('adminToken')}` },
            });
            setCandidateData((prev) => ({
                ...prev,
                jd_tags: (prev?.jd_tags || []).filter((tag) => tag.job_description_id !== jobDescriptionId),
            }));
            setSnackbar({
                open: true,
                message: t('candidateDetails.jdTagRemoved'),
                severity: 'success',
            });
        } catch (err) {
            setSnackbar({
                open: true,
                message: err.response?.data?.error || t('candidateDetails.jdTagRemoveFailed'),
                severity: 'error',
            });
        } finally {
            setRemovingId(null);
        }
    };

    return (
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6">
            <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center">
                    <Tag className="w-5 h-5 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900" title={t('candidateDetails.jobDescriptionsTooltip')}>{t('candidateDetails.jdTags')}</h3>
            </div>
            <p className="text-sm text-gray-600 mb-4">{t('candidateDetails.jdTagsHelp')}</p>
            <div className="flex flex-wrap gap-2 mb-4">
                {jdTags.length === 0 ? (
                    <span className="text-sm text-gray-400">{t('candidateDetails.noJdTags')}</span>
                ) : (
                    jdTags.map((tag) => (
                        <span
                            key={tag.id}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium bg-indigo-50 text-indigo-700 border border-indigo-200"
                        >
                            <button
                                type="button"
                                onClick={async (e) => {
                                    e.stopPropagation();
                                    setJdDescriptionLoading(true);
                                    try {
                                        const res = await api.get(`job-descriptions/${tag.job_description_id}/`);
                                        const desc = res?.data?.description ?? '';
                                        setJdDescriptionModal({
                                            open: true,
                                            title: res?.data?.title ?? tag.title,
                                            content: parse(convertMarkdownToHtml(desc)),
                                        });
                                    } catch (err) {
                                        setSnackbar({ open: true, message: err.response?.data?.detail || 'Failed to load description', severity: 'error' });
                                    } finally {
                                        setJdDescriptionLoading(false);
                                    }
                                }}
                                disabled={jdDescriptionLoading}
                                className="text-left hover:underline cursor-pointer disabled:opacity-50"
                                title={t('candidateDetails.jobDescriptionsTooltip')}
                            >
                                {tag.title}
                            </button>
                            <button
                                type="button"
                                onClick={() => handleRemove(tag.job_description_id)}
                                disabled={removingId === tag.job_description_id}
                                className="p-0.5 rounded hover:bg-indigo-200 transition-colors disabled:opacity-50"
                                title={t('candidateDetails.remove')}
                            >
                                {removingId === tag.job_description_id ? (
                                    <Loader2 size={14} className="animate-spin" />
                                ) : (
                                    <X size={14} />
                                )}
                            </button>
                        </span>
                    ))
                )}
            </div>
            {availableJDs.length > 0 && (
                <div className="flex items-center gap-2">
                    <div className="relative flex-1 min-w-[200px]">
                        <div className="relative group" ref={buttonRef}>
                            <input
                                type="text"
                                placeholder={t('candidateDetails.addJdTag')}
                                value={jdSearch}
                                onChange={(e) => {
                                    setJdSearch(e.target.value);
                                    setIsDropdownOpen(true);
                                }}
                                onFocus={() => setIsDropdownOpen(true)}
                                className="w-full pl-3 pr-8 py-2 text-sm bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all shadow-sm group-hover:border-gray-400"
                            />
                            {addSelectValue && (
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setAddSelectValue('');
                                        setJdSearch('');
                                        setIsDropdownOpen(false);
                                    }}
                                    className="absolute right-8 top-1/2 transform -translate-y-1/2 p-1 hover:bg-gray-100 rounded-full text-gray-400 hover:text-gray-600 transition-colors"
                                >
                                    <X size={12} />
                                </button>
                            )}
                            <button
                                type="button"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setIsDropdownOpen(!isDropdownOpen);
                                }}
                                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors focus:outline-none"
                            >
                                <ChevronDown size={14} className={`transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`} />
                            </button>
                        </div>

                        {isDropdownOpen && (
                            <div
                                ref={dropdownListRef}
                                className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl z-50 max-h-[160px] overflow-y-auto overflow-x-hidden animate-in fade-in slide-in-from-top-1 duration-200"
                            >
                                <div className="p-1">
                                    {availableJDs
                                        .filter(jd => jd.title.toLowerCase().includes(jdSearch.toLowerCase()))
                                        .map((jd) => (
                                            <div
                                                key={jd.id}
                                                onClick={() => {
                                                    setAddSelectValue(jd.id.toString());
                                                    setJdSearch(jd.title);
                                                    setIsDropdownOpen(false);
                                                }}
                                                className={`px-3 py-2 text-sm hover:bg-indigo-50 cursor-pointer truncate transition-colors rounded-md ${addSelectValue === jd.id.toString() ? 'bg-indigo-50 text-indigo-700 font-medium' : 'text-gray-700'}`}
                                            >
                                                {jd.title}
                                            </div>
                                        ))}
                                    {availableJDs.filter(jd => jd.title.toLowerCase().includes(jdSearch.toLowerCase())).length === 0 && (
                                        <div className="px-3 py-6 text-center text-xs text-gray-400 italic">
                                            No matching Job Descriptions
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                    <button
                        type="button"
                        onClick={handleAdd}
                        disabled={!addSelectValue || adding}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                        {adding ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
                        {t('candidateDetails.add')}
                    </button>
                </div>
            )}
            <ShowDescriptionDialog
                open={jdDescriptionModal.open}
                onClose={() => setJdDescriptionModal({ open: false, title: '', content: null })}
                content={jdDescriptionModal.content}
                title={jdDescriptionModal.title}
            />
        </div>
    );
}
